import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "src-next/modules/crm-clinical/messageTemplates/constants";
import { messageTemplatesService } from "src-next/modules/crm-clinical/messageTemplates/services/messageTemplatesService";
import { filterMessageTemplatesByOrganization } from "src-next/modules/crm-clinical/messageTemplates/utils/messageTemplateUiUtils";
import { runSendWhatsAppMessageOrchestrator } from "src-next/modules/crm-clinical/messaging/helpers/sendWhatsAppMessageOrchestrator";
import { doesTemplateRequireQuestionnaire } from "src-next/modules/crm-clinical/messaging/helpers/templateQuestionnaireHelpers";
import { endpointService } from "src-next/modules/crm-clinical/messaging/services/endpointService";
import type {
  MessagingAppointment,
  MessagingPatient,
  SendMessageResult,
} from "src-next/modules/crm-clinical/messaging/types";
import { crmMessageFranchiseStatsService } from "src-next/modules/crm-clinical/statistics/services/crmMessageFranchiseStatsService";
import { questionnaireManagementService } from "src-next/modules/questionnaire-builder/questionnaire-management/services";

import {
  buildAutomationContext,
  buildMessagingAppointment,
  buildQuestionnaireOptionFromTemplate,
  findAutomationMessageTemplateForSend,
  logAutomationTriggerError,
  logAutomationTriggerSkip,
  mapMessageTemplateToMessagingTemplate,
  normalizeAutomationOrganizationId,
  resolveFluxzapRequesterReference,
  type AutomationMessagingTriggerId,
  type AutomationTriggerSkipResult,
} from "../helpers/automationMessagingTriggerHelpers";
import { automationService } from "./automationService";
import type { AutomationConfig, AutomationContext } from "../types";

export type TriggerAutomationMessagingParams = {
  automationId: AutomationMessagingTriggerId;
  context: AutomationContext;
  appointment: MessagingAppointment;
  patient: MessagingPatient;
  requesterReference?: string | null;
};

export type TriggerAutomationMessagingResult =
  | SendMessageResult
  | AutomationTriggerSkipResult;

/**
 * Orquestra disparo automático de mensagem WhatsApp para automações clínicas.
 *
 * Best-effort: retorna `{ status: "skipped" }` quando pré-condições falham,
 * em vez de lançar exceção — callers legados não devem ser bloqueados.
 */
export async function triggerAutomationMessaging(
  params: TriggerAutomationMessagingParams,
): Promise<TriggerAutomationMessagingResult> {
  const organizationId = normalizeAutomationOrganizationId(
    params.context.organizationId,
  );
  if (!organizationId) {
    const reason = "organizationId ausente para automação.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const automationContext = buildAutomationContext({
    organizationId,
    practitionerId: params.context.practitionerId,
    whatsappBusinessAccountId: params.context.whatsappBusinessAccountId,
  });
  if (!automationContext) {
    const reason = "Contexto de automação incompleto (org/profissional).";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const requesterReference = resolveFluxzapRequesterReference(
    params.requesterReference,
  );
  if (!requesterReference) {
    const reason = "requesterReference (PractitionerRole) não configurado.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  // Verifica se automação está configurada e habilitada para o contexto.
  const automationItems = await automationService.listByContext({
    context: automationContext,
  });
  const automationItem = automationItems.find(
    (item) => item.id === params.automationId,
  );
  if (!automationItem?.isConfigured || !automationItem.isEnabled) {
    const reason = `Automação "${params.automationId}" não configurada ou desabilitada.`;
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const config = await automationService.getConfigByAutomationId({
    context: automationContext,
    automationId: params.automationId,
  });
  if (!config?.messageTemplateId) {
    const reason = `Automação "${params.automationId}" sem template configurado.`;
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  // Paridade com SendWhatsAppMessageDialog + useCrmSendMessageFranchiseGate:
  // - Sem endpoint Meta ativo: envio segue via fallback FluxZap (DEFAULT WABA / phone-number-id).
  // - Gate de franquia só quando NÃO há endpoint Meta (`requiresGate = gateEnabled && !hasActiveMetaEndpoint`).
  const [endpointConfig, activeEndpoint] = await Promise.all([
    endpointService.getWhatsAppEndpointConfigByContext({ organizationId }),
    endpointService.getActiveWhatsAppEndpointByContext({ organizationId }),
  ]);
  const hasActiveMetaEndpoint = Boolean(
    activeEndpoint?.id && activeEndpoint.status === "active",
  );
  const requiresFranchiseGate = Boolean(organizationId) && !hasActiveMetaEndpoint;

  if (requiresFranchiseGate) {
    const franchiseStats =
      await crmMessageFranchiseStatsService.fetchCrmMessageFranchiseUsageStats(
        organizationId,
      );
    if ((franchiseStats.remainingMessagesForSend ?? 0) <= 0) {
      const reason = "Saldo de mensagens CRM insuficiente para envio automático.";
      logAutomationTriggerSkip(reason);
      return { status: "skipped", reason };
    }
  }

  const wabaId =
    endpointConfig.wabaIdRank1?.trim() ||
    params.context.whatsappBusinessAccountId?.trim() ||
    DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const [rawTemplates, questionnaires] = await Promise.all([
    messageTemplatesService.list({ whatsappBusinessAccountId: wabaId }),
    questionnaireManagementService.listByOrganizationIdentifier(organizationId),
  ]);

  // organizationId no CRM pode vir com ou sem prefixo OID — filterMessageTemplatesByOrganization aceita ambos.
  const orgTemplates = filterMessageTemplatesByOrganization(
    rawTemplates,
    organizationId,
  );
  const messageTemplate = findAutomationMessageTemplateForSend({
    templates: orgTemplates,
    messageTemplateId: config.messageTemplateId,
    organizationId,
  });
  if (!messageTemplate) {
    const reason = `Template "${config.messageTemplateId}" não encontrado ou inelegível para envio.`;
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const messagingTemplate = mapMessageTemplateToMessagingTemplate(messageTemplate);
  const questionnaireNameById = new Map(
    questionnaires
      .filter((item) => Boolean(item.id))
      .map((item) => [item.id as string, item.title]),
  );
  const questionnaireOption = buildQuestionnaireOptionFromTemplate({
    template: messageTemplate,
    questionnaireNameById,
  });

  if (
    doesTemplateRequireQuestionnaire(messagingTemplate) &&
    !questionnaireOption?.id
  ) {
    const reason =
      "Template exige questionário, mas nenhum questionário vinculado foi encontrado.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const appointment = buildMessagingAppointment({
    appointmentId: params.appointment.id,
    startAtIso: params.appointment.startAtIso,
  });

  try {
    const result = await runSendWhatsAppMessageOrchestrator(
      {
        patient: params.patient,
        appointment,
        template: messagingTemplate,
        questionnaire: questionnaireOption,
        requesterReference,
        automationConfig: config as AutomationConfig,
      },
      {
        wabaIdRank1: endpointConfig.wabaIdRank1,
        phoneNumberIdRank1: endpointConfig.phoneNumberIdRank1,
      },
    );

    if (result.status === "error") {
      logAutomationTriggerError(
        `Falha no envio automático (${params.automationId}): ${result.errorMessage}`,
      );
    }

    return result;
  } catch (error) {
    logAutomationTriggerError(
      `Erro inesperado no envio automático (${params.automationId}).`,
      error,
    );
    return {
      status: "error",
      errorMessage: "Erro ao enviar mensagem automática.",
    };
  }
}
